import { useCallback, useContext, useEffect, useState } from "react";
import AuthContext from "@/contexts/AuthContext";
import { RankingsService } from "@/core/http/services/RankingsService";

const normalizeRanking = (ranking) => ({
  id: ranking?.id ?? null,
  name: ranking?.name ?? "Ranking",
  year: ranking?.year ?? null,
});

const normalizeStanding = (item, index, currentPlayerId) => {
  const player = item?.player ?? item;
  const id = player?.id ?? `player-${index}`;

  return {
    id,
    position: item?.position ?? index + 1,
    name: player?.name ?? "Jogador",
    points: item?.points ?? 0,
    wins: item?.wins ?? 0,
    losses: item?.losses ?? 0,
    matchesPlayed: item?.matchesPlayed ?? 0,
    saldo: item?.saldo ?? 0,
    avatarUrl: player?.avatarUrl ?? null,
    categoryId: player?.category?.id ?? null,
    categoryName: player?.category?.name ?? null,
    categoryType: player?.category?.type ?? null,
    isCurrentUser: Boolean(currentPlayerId && id === currentPlayerId),
  };
};

const useRankingUser = () => {
  const { userLoggedData } = useContext(AuthContext);
  const playerId = userLoggedData?.playerId ?? null;
  const defaultCategoryId =
    userLoggedData?.currentCategory?.id ?? userLoggedData?.category?.id ?? null;

  const [loading, setLoading] = useState({
    rankings: false,
    players: false,
  });
  const [error, setError] = useState({ type: null, message: null });

  const [rankingsData, setRankingsData] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [standingsByCacheKey, setStandingsByCacheKey] = useState({});
  const [selectedCategoryByRankingId, setSelectedCategoryByRankingId] = useState({});

  const getRankings = useCallback(async () => {
    setLoading((prev) => ({ ...prev, rankings: true }));
    setError({ type: null, message: null });

    try {
      const { status, data, message } = await RankingsService.getAll();

      if (data?.error || status !== 200) {
        setError({ type: "rankings", message: message || "Erro ao buscar os rankings." });
        setRankingsData([]);
        return [];
      }

      const normalized = (Array.isArray(data) ? data : []).map(normalizeRanking);
      setRankingsData(normalized);
      return normalized;
    } catch (err) {
      setError({ type: "rankings", message: err?.message || "Erro inesperado ao buscar os rankings." });
      setRankingsData([]);
      return [];
    } finally {
      setLoading((prev) => ({ ...prev, rankings: false }));
    }
  }, []);

  const ensureDefaultFilters = useCallback(
    (rankings = [], categories = []) => {
      const fallbackCategoryId = categories.find((c) => c.id === defaultCategoryId)
        ? defaultCategoryId
        : categories[0]?.id ?? null;

      setSelectedCategoryByRankingId((prev) => {
        const next = { ...prev };
        rankings.forEach((ranking) => {
          if (
            next[ranking.id] === undefined &&
            fallbackCategoryId !== null &&
            fallbackCategoryId !== undefined
          ) {
            next[ranking.id] = fallbackCategoryId;
          }
        });
        return next;
      });
    },
    [defaultCategoryId],
  );

  const setCategoryForRanking = useCallback((rankingId, categoryId) => {
    setSelectedCategoryByRankingId((prev) => ({ ...prev, [rankingId]: categoryId }));
  }, []);

  const getStandings = useCallback(
    async (rankingId, categoryId, { force = false } = {}) => {
      if (rankingId === null || rankingId === undefined) return;
      const cacheKey = `${rankingId}_${categoryId || ""}`;
      if (!force && standingsByCacheKey[cacheKey]) return;

      setLoading((prev) => ({ ...prev, players: true }));
      setError({ type: null, message: null });

      try {
        const normalizedCategoryId =
          categoryId === "" || categoryId === null || categoryId === undefined
            ? undefined
            : categoryId;

        const { status, data, message } = await RankingsService.getStandings(
          rankingId,
          normalizedCategoryId,
        );

        if (data?.error || status !== 200) {
          setError({ type: "players", message: message || "Erro ao buscar o ranking." });
          setStandingsByCacheKey((prev) => ({ ...prev, [cacheKey]: [] }));
          return;
        }

        const raw = Array.isArray(data?.standings) ? data.standings : [];
        const normalized = raw.map((item, index) => normalizeStanding(item, index, playerId));

        setStandingsByCacheKey((prev) => ({ ...prev, [cacheKey]: normalized }));

        // Derive categories from standings (only when fetching without category filter)
        if (normalizedCategoryId === undefined) {
          const categoryMap = new Map();
          normalized.forEach((p) => {
            if (p.categoryId !== null && p.categoryId !== undefined) {
              categoryMap.set(p.categoryId, {
                id: p.categoryId,
                name: p.categoryName ?? "Categoria",
                type: p.categoryType ?? "",
              });
            }
          });

          setCategoriesData((prev) => {
            const existing = new Map(prev.map((c) => [c.id, c]));
            categoryMap.forEach((cat, id) => {
              if (!existing.has(id)) existing.set(id, cat);
            });
            return Array.from(existing.values());
          });
        }
      } catch (err) {
        setError({ type: "players", message: err?.message || "Erro inesperado ao buscar o ranking." });
        setStandingsByCacheKey((prev) => ({ ...prev, [cacheKey]: [] }));
      } finally {
        setLoading((prev) => ({ ...prev, players: false }));
      }
    },
    [playerId, standingsByCacheKey],
  );

  const initialize = useCallback(async () => {
    const rankings = await getRankings();
    ensureDefaultFilters(rankings, []);
  }, [ensureDefaultFilters, getRankings]);

  useEffect(() => {
    if (categoriesData.length === 0 || rankingsData.length === 0) return;
    ensureDefaultFilters(rankingsData, categoriesData);
  }, [categoriesData, rankingsData, ensureDefaultFilters]);

  const getStandingsForRanking = useCallback(
    (rankingId, categoryId) => {
      const cacheKey = `${rankingId}_${categoryId || ""}`;
      return standingsByCacheKey[cacheKey] || [];
    },
    [standingsByCacheKey],
  );

  return {
    playerId,
    defaultCategoryId,
    rankingsData,
    categoriesData,
    selectedCategoryByRankingId,
    setCategoryForRanking,
    getStandingsForRanking,
    getStandings,
    loading,
    error,
    initialize,
  };
};

export default useRankingUser;
